// NOTE: need a way to keep the params (e.g. app=ios, USER_ID, etc)
// So that if the user logs out and go back to /signin, the params
// are still there.

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, Redirect } from 'react-router-dom';
import firebase from '../firebase';
import { signIn } from '../audiblogsApi';
import userStore from '../Stores/User';
import queryString from 'query-string';
import mixpanel from 'mixpanel-browser';
import logo from '../logo-no-text.png';
import loading from '../Assets/loading.gif';
import { observer } from 'mobx-react';
import { version } from '../../package.json';
import './Signin.scss';
import { setLandingPage } from '../utils/trackingParams';

const SignIn = observer(({ location, match }) => {
  const { logOut, user, firebaseUser, idToken } = userStore;
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isNewUserSignup, setIsNewUserSignup] = useState(false);
  const [isSignIn, setIsSignIn] = useState(false);  // Default to sign up
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasTrackedSignup = useRef(false);

  const params = useMemo(() => queryString.parse(location.search), [location.search]);
  const app = params.app;

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        // Track successful signup (only once)
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'google'
        });
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }

      if (result.user?.email) {
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
        const distinctId = mixpanel.get_distinct_id();
        
        const existingLandingPage = mixpanel.get_property('utm_landingpage');
        const existingInitialLandingPage = mixpanel.get_property('initial_landingpage');
        
        const userProps = {
          email: result.user.email,
          name: result.user.displayName,
          button_clicked: params.source,
          ...storedUtmParams,
          utm_landingpage: storedUtmLandingPage || existingLandingPage,
          initial_landingpage: storedUtmLandingPage || existingInitialLandingPage
        };
        
        mixpanel.alias(distinctId);
        mixpanel.people.set(userProps);
        mixpanel.register_once(userProps);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* Temporarily removed other auth handlers
  const handleMicrosoftSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new firebase.auth.OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await firebase.auth().signInWithPopup(provider);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        // Track successful signup (only once)
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'microsoft'
        });
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }

      if (result.user?.email) {
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
        const distinctId = mixpanel.get_distinct_id();
        
        const existingLandingPage = mixpanel.get_property('utm_landingpage');
        const existingInitialLandingPage = mixpanel.get_property('initial_landingpage');
        
        const userProps = {
          email: result.user.email,
          name: result.user.displayName,
          button_clicked: params.source,
          ...storedUtmParams,
          utm_landingpage: storedUtmLandingPage || existingLandingPage,
          initial_landingpage: storedUtmLandingPage || existingInitialLandingPage
        };
        
        mixpanel.alias(distinctId);
        mixpanel.people.set(userProps);
        mixpanel.register_once(userProps);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new firebase.auth.FacebookAuthProvider();
      provider.addScope('email');
      
      const result = await firebase.auth().signInWithPopup(provider);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        // Track successful signup (only once)
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'facebook'
        });
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }

      if (result.user?.email) {
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
        const distinctId = mixpanel.get_distinct_id();
        
        const userProps = {
          email: result.user.email,
          name: result.user.displayName,
          button_clicked: params.source,
          ...storedUtmParams,
          utm_landingpage: storedUtmLandingPage || mixpanel.get_property('utm_landingpage'),
          initial_landingpage: storedUtmLandingPage || mixpanel.get_property('initial_landingpage')
        };
        
        mixpanel.alias(distinctId);
        mixpanel.people.set(userProps);
        mixpanel.register_once(userProps);
      }
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with the same email address. Try signing in with a different method.');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwitterSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new firebase.auth.TwitterAuthProvider();
      
      const result = await firebase.auth().signInWithPopup(provider);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        // Track successful signup (only once)
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'twitter'
        });
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }

      if (result.user) {
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
        const distinctId = mixpanel.get_distinct_id();
        
        const userProps = {
          name: result.user.displayName,
          email: result.user.email || '',
          button_clicked: params.source,
          ...storedUtmParams,
          utm_landingpage: storedUtmLandingPage || mixpanel.get_property('utm_landingpage'),
          initial_landingpage: storedUtmLandingPage || mixpanel.get_property('initial_landingpage')
        };
        
        mixpanel.alias(distinctId);
        mixpanel.people.set(userProps);
        mixpanel.register_once(userProps);
      }
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with the same email address. Try signing in with a different method.');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  */

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');
      if (isSignIn) {
        await firebase.auth().signInWithEmailAndPassword(email, password);
      } else {
        const result = await firebase.auth().createUserWithEmailAndPassword(email, password);
        
        // Track successful signup (only once)
        if (!hasTrackedSignup.current) {
          hasTrackedSignup.current = true;
          mixpanel.track('Sign Up Success', {
            ...params,
            variant: params.redirected_from ? 'b' : 'a',
            source: params.source || 'direct',
            method: 'email'
          });
        }
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        if (result.user?.email) {
          const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
          const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
          const distinctId = mixpanel.get_distinct_id();
          
          const userProps = {
            email: result.user.email,
            button_clicked: params.source,
            ...storedUtmParams,
            utm_landingpage: storedUtmLandingPage,
            initial_landingpage: storedUtmLandingPage
          };
          
          mixpanel.alias(distinctId);
          mixpanel.people.set(userProps);
          mixpanel.register_once(userProps);
        }
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const redirectTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('redirected_from');
    
    let baseUrl = '/';
    if (isNewUserSignup) {
      baseUrl = `/onboarding/ChooseVoice`;
      searchParams.set('variant', params.redirected_from ? 'b' : 'a');
      if (params.redirected_from) {
        searchParams.set('redirected_from', params.redirected_from);
      }
    } else if (params.redirected_from === 'setup') {
      baseUrl = '/setup';
    } else if (params.redirected_from === 'newsletter') {
      baseUrl = '/newsletter';
      searchParams.set('article_url', params.article_url);
      searchParams.set('article_title', params.article_title);
    } else if (params.redirected_from === 'subscription') {
      baseUrl = '/subscription';
      searchParams.set('chars', params.chars);
    } else if (params.redirected_from === 'link') {
      baseUrl = '/link';
      searchParams.set('email', params.email);
    } else if (params.redirected_from === 'add') {
      baseUrl = '/add';
      searchParams.set('url', params.url);
    } else if (params.redirected_from === 'setupnew') {
      baseUrl = '/setupnew';
    } else if (params.redirected_from === 'trial') {
      baseUrl = '/add';
      searchParams.set('article_url', params.article_url);
    } else if (params.redirected_from === 'drawabox') {
      baseUrl = '/drawabox';
    } else if (params.redirected_from === 'onboarding') {
      baseUrl = '/onboarding/howtoclip';
    } else if (params.redirected_from === 'preferredVoices') {
      baseUrl = '/preferredVoices';
    } else if (params.redirected_from === 'onboarding/quickstart/2') {
      baseUrl = '/onboarding/quickstart/2';
    } else if (params.redirected_from === 'signup') {
      baseUrl = '/onboarding/ChooseVoice';
      if (params.variant) {
        searchParams.set('variant', params.variant);
      }
    }

    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }, [params, isNewUserSignup, location.search]);

  useEffect(() => {
    mixpanel.track('Open Signin Page', {
      ...params,
      variant: params.redirected_from ? 'b' : 'a',
      source: params.source || 'direct'
    });
  }, []);

  useEffect(() => {
    if (user) {
      if (params.redirected_from === 'drawabox') {
        mixpanel.track('Successful Login After Subscription');
      } else {
        setShouldRedirect(true);
      }
    }

    if (firebaseUser && !firebaseUser.email) {
      logOut();
      alert('Unable to get the email address. Please use a different sign-in method.');
    }
  }, [firebaseUser, user, idToken, match.params.USER_ID, logOut, redirectTo, params.redirected_from]);

  if (shouldRedirect || userStore.isSignedIn) {
    return <Redirect to={redirectTo} />;
  }

  if (params.app === 'ios') {
    return <Redirect to={`/shortcuts`} />;
  }

  if (userStore.isSignedIn === null) {
    return (
      <div className="signin-container">
        <img src={loading} style={{ height: '50px' }} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className="signin-container">
      <img src={logo} className="logo" alt="Audioread logo" style={{ marginBottom: '30px' }} />
      
      <div className="signin-card">
        <h1>Welcome to Audioread</h1>

        <button onClick={handleGoogleSignIn} className="google-button" disabled={isLoading}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
          <span>Continue with Google</span>
        </button>

        {/* Temporarily removed other auth providers
        <button onClick={handleMicrosoftSignIn} className="microsoft-button" disabled={isLoading}>
          <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgdmlld0JveD0iMCAwIDIxIDIxIj48cGF0aCBmaWxsPSIjZjI1MDIyIiBkPSJNMSAxaDl2OUgxeiIvPjxwYXRoIGZpbGw9IiMwMGE0ZWYiIGQ9Ik0xMSAxaDl2OWgtOXoiLz48cGF0aCBmaWxsPSIjN2ZiYTAwIiBkPSJNMSAxMWg5djloLTl6Ii8+PHBhdGggZmlsbD0iI2ZmYjkwMCIgZD0iTTExIDExaDl2OWgtOXoiLz48L3N2Zz4=" alt="Microsoft" />
          <span>Continue with Microsoft</span>
        </button>

        <button onClick={handleFacebookSignIn} className="facebook-button" disabled={isLoading}>
          <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjZmZmZmZmIiBkPSJNMjQgMTJjMC02LjYyNy01LjM3My0xMi0xMi0xMlMwIDUuMzczIDAgMTJjMCA1Ljk5IDQuMzg4IDEwLjk1NCAxMC4xMjUgMTEuODU0VjE1LjQ3SDcuMDc4VjEyaDMuMDQ3VjkuMzU2YzAtMy4wMDcgMS43OTItNC42NjggNC41MTgtNC42NjhjMS4zMSAwIDIuNjguMjM0IDIuNjguMjM0djIuOTUzSDE1LjgzYy0xLjQ5IDAtMS45NTUuOTI1LTEuOTU1IDEuODc0VjEyaDMuMzI4bC0uNTMyIDMuNDdoLTIuNzk2djguMzg1QzE5LjYxMiAyMi45NTQgMjQgMTcuOTkgMjQgMTJ6Ii8+PC9zdmc+" alt="Facebook" />
          <span>Continue with Facebook</span>
        </button>

        <button onClick={handleTwitterSignIn} className="x-button" disabled={isLoading}>
          <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjZmZmZmZmIiBkPSJNMTguMjQ0IDIuMjVoMy43OGwtOC4zMTYgOS42MkwyMi4xMjggMjJoLTcuMjFsLTUuMzQtNi4zMjZMMy40MiAyMkgwbDguOTA2LTEwLjI4TDAuMzM2IDIuMjVoNy4zODRsNC44MiA1LjczNGw1LjcwNC01LjczNHptLTEuNTQyIDE3LjhIMTkuNDJMNi4wNiA0LjAyN0g0LjI5M0wxNi43IDIwLjA1eiIvPjwvc3ZnPg==" alt="X (formerly Twitter)" />
          <span>Continue with X</span>
        </button>
        */}

        <div className="divider">
          <span>or</span>
        </div>

        <form onSubmit={handleEmailSignIn}>
          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              style={{ textAlign: 'left', width: '235px' }}
            />
          </div>

          <div className="input-group">
            <div className="password-header">
              <label htmlFor="password">Password</label>
              {isSignIn && (
                <Link to="/reset-password" className="forgot-password">
                  Forgot Password?
                </Link>
              )}
            </div>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{ textAlign: 'left' }}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.99989 3L19.9999 21M9.8433 9.91364C9.32066 10.4536 8.99989 11.1892 8.99989 12C8.99989 13.6569 10.3431 15 11.9999 15C12.8031 15 13.5316 14.6741 14.0681 14.1471M6.49989 6.64715C4.59628 7.90034 3.15271 9.78394 2.45117 12C3.73989 16.0571 7.52989 19 11.9999 19C13.9313 19 15.7349 18.4194 17.2894 17.4184M10.9999 5.04939C11.3277 5.01673 11.6613 5 11.9999 5C16.4699 5 20.2599 7.94291 21.5486 12C21.2217 13.0177 20.7457 13.9615 20.1454 14.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2.45801 12C3.73228 7.94288 7.52257 5 12.0002 5C16.4778 5 20.2681 7.94291 21.5424 12C20.2681 16.0571 16.4778 19 12.0002 19C7.52256 19 3.73226 16.0571 2.45801 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ color: '#ef4444', marginBottom: '20px', fontSize: '14px' }}>
              {error}
            </div>
          )}

          <button type="submit" className="signin-button" disabled={isLoading}>
            {isLoading ? 'Loading...' : isSignIn ? 'Sign In' : 'Start for free'}
          </button>
        </form>

        <button 
          onClick={() => setIsSignIn(!isSignIn)} 
          className="toggle-signin-link"
        >
          {isSignIn ? 'Or sign up instead' : 'Or sign in instead'}
        </button>
      </div>

      <div className="footer">
        By continuing, you agree to our{' '}
        <Link to="/terms">Terms of Service</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>
      </div>
    </div>
  );
});

export default SignIn;
